"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

export interface ToggleSwitchProps {
  /** Whether the switch is checked/on */
  checked?: boolean;
  /** Callback when the switch value changes */
  onChange?: (checked: boolean) => void;
  /** Whether the switch is disabled */
  disabled?: boolean;
  /** Whether the switch is in an invalid/error state */
  invalid?: boolean;
  /** Additional class names */
  className?: string;
  /** ID for the input element */
  id?: string;
  /** Name attribute for form submission */
  name?: string;
  /** Accessible label for screen readers */
  "aria-label"?: string;
  /** ID of element that labels this switch */
  "aria-labelledby"?: string;
}

/**
 * ToggleSwitch - A boolean toggle switch component
 *
 * Features:
 * - Checked/unchecked states
 * - Hover states with color transitions
 * - Focus-visible ring with offset
 * - Disabled state
 * - Invalid/error state with red border
 * - Smooth handle animation
 *
 * @example
 * ```tsx
 * <ToggleSwitch
 *   checked={isEnabled}
 *   onChange={(checked) => setIsEnabled(checked)}
 * />
 * ```
 */
const ToggleSwitch = React.forwardRef<HTMLButtonElement, ToggleSwitchProps>(
  (
    {
      checked = false,
      onChange,
      disabled = false,
      invalid = false,
      className,
      id,
      name,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
    },
    ref
  ) => {
    const handleClick = () => {
      if (!disabled && onChange) {
        onChange(!checked);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        handleClick();
      }
    };

    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        id={id}
        name={name}
        aria-checked={checked}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-invalid={invalid}
        disabled={disabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          // Base styles
          "relative inline-flex shrink-0 cursor-pointer items-center justify-start",
          "w-[var(--toggle-width)] h-[var(--toggle-height)]",
          "rounded-[var(--toggle-radius)]",
          "transition-colors duration-200 ease-in-out",
          
          // Border for invalid state
          invalid ? "border border-(--toggle-border-invalid)" : "border border-transparent",
          
          // Background colors based on state
          !disabled && !checked && "bg-(--toggle-bg) hover:bg-(--toggle-bg-hover)",
          !disabled && checked && "bg-(--toggle-bg-checked) hover:bg-(--toggle-bg-checked-hover)",
          disabled && "bg-(--toggle-bg-disabled) cursor-not-allowed",
          
          // Focus-visible ring with offset
          "focus:outline-none",
          "focus-visible:ring-[3px] focus-visible:ring-(--toggle-focus-ring)",
          "focus-visible:ring-offset-2 focus-visible:ring-offset-(--toggle-focus-offset)",
          
          className
        )}
      >
        {/* Handle */}
        <span
          className={cn(
            "pointer-events-none inline-block",
            "size-[var(--toggle-handle-size)]",
            "rounded-[var(--toggle-handle-radius)]",
            "shadow-[var(--toggle-handle-shadow)]",
            "transform transition-transform duration-200 ease-in-out",
            
            // Handle background
            disabled ? "bg-(--toggle-handle-bg-disabled)" : "bg-(--toggle-handle-bg)",
            
            // Handle position
            checked
              ? "translate-x-[calc(var(--toggle-width)-var(--toggle-handle-size)-var(--toggle-gap))]"
              : "translate-x-[var(--toggle-gap)]"
          )}
        />
        
        {/* Inner shadow overlay */}
        <span
          className="absolute inset-0 pointer-events-none rounded-[var(--toggle-radius)] shadow-[var(--toggle-inner-shadow)]"
          aria-hidden="true"
        />
      </button>
    );
  }
);

ToggleSwitch.displayName = "ToggleSwitch";

export { ToggleSwitch };

